import { Link } from "@tanstack/react-router";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Music2,
} from "lucide-react";
import footerLogo from "@/assets/colorado-web-impressions-footer-logo-colorado-springs.webp";


// X (formerly Twitter) logo — Lucide doesn't ship the post-rebrand mark,
// so we render the official glyph as an inline SVG that inherits currentColor.
const XIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

// Yelp brand mark — Lucide doesn't ship brand logos, so we render the
// official glyph as an inline SVG that inherits currentColor.
const YelpIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path d="M20.16 12.594l-1.94-.636c-1.39-.44-2.27 1.62-.92 2.27l1.81.94c.5.26 1.13.13 1.46-.31l.13-.18c.59-.85.45-1.74-.54-2.08zm-3.27 4.49l-1.4-1.4c-.99-1-2.65.49-1.93 1.74l1 1.79c.27.5.9.7 1.41.45l.21-.1c.99-.45 1.36-1.31.71-2.48zm-3.07-7.51l3.83-6.65c.27-.47.05-1.07-.46-1.27-1.21-.45-3.13-.79-4.95-.43-.51.1-.84.61-.74 1.13l1.27 7.41c.16.93 1.43 1.04 1.86.16l-.81-.35zm-3.33 4.21l-5.74-1.94c-.49-.17-1.04.13-1.16.64-.34 1.4-.32 3.18.32 4.74.21.51.84.71 1.32.41l5.34-3.32c.79-.49.65-1.69-.18-1.98zm.42-2.78L4.65 7.18c-.46-.32-1.1-.13-1.34.39-.61 1.31-.97 3.07-.78 4.78.06.55.6.91 1.13.78l6.62-1.66c.91-.23.95-1.49.05-1.78z"/>
  </svg>
);

const socialLinks = [
  { Icon: Facebook, href: "https://www.facebook.com/coloradowebimpressions/", label: "Facebook" },
  { Icon: Instagram, href: "https://www.instagram.com/coloradowebimpressions/", label: "Instagram" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/colorado-web-impressions/", label: "LinkedIn" },
  { Icon: Youtube, href: "https://www.youtube.com/channel/UCobHjvCeHGsOsHFDp9cAOlQ", label: "YouTube" },
  { Icon: XIcon, href: "https://x.com/cwebimpressions", label: "X (formerly Twitter)" },
  { Icon: Music2, href: "https://open.spotify.com/show/1QEaGYsfCspVL17n2CCje4", label: "Spotify Podcast" },
  { Icon: YelpIcon, href: "https://www.yelp.com/biz/colorado-web-impressions-colorado-springs", label: "Yelp" },
];

const cityLinks: Array<[string, string]> = [
  ["Colorado Springs SEO", "/colorado-springs-seo"],
  ["Denver SEO", "/denver-seo"],
  ["Fort Collins SEO", "/fort-collins-seo"],
  ["Boulder SEO", "/boulder-seo"],
  ["Aurora SEO", "/aurora-seo"],
  ["Pueblo SEO", "/pueblo-seo"],
  ["Castle Rock SEO", "/castle-rock-seo"],
  ["Parker SEO", "/parker-seo"],
  ["Colorado Springs Web Design", "/colorado-springs-web-design"],
  ["Denver Web Design", "/web-design-denver-colorado"],
  ["Fort Collins Web Design", "/web-design-fort-collins-colorado"],
  ["Boulder Web Design", "/web-design-boulder-colorado"],
];

const industryLinks: Array<[string, string]> = [
  ["HVAC Marketing Colorado", "/hvac-marketing-colorado"],
  ["Plumbing Marketing Colorado", "/plumbing-marketing-colorado"],
  ["Roofing Marketing Colorado", "/roofing-marketing-colorado"],
  ["Electrical Contractor Marketing", "/electrical-contractor-marketing-colorado"],
  ["Landscaping Marketing Colorado", "/landscaping-marketing-colorado"],
  ["Concrete Marketing Colorado", "/concrete-marketing-colorado"],
  ["Painting Marketing Colorado", "/painting-marketing-colorado"],
  ["Garage Door Marketing Colorado", "/garage-door-marketing-colorado"],
  ["Moving Company Marketing Colorado", "/moving-company-marketing-colorado"],
  ["Dental Marketing Colorado", "/dental-marketing-colorado"],
  ["Medical Marketing Colorado", "/medical-marketing-colorado"],
  ["Chiropractic Marketing Colorado", "/chiropractic-marketing-colorado"],
  ["Law Firm SEO Colorado", "/law-firm-seo-colorado"],
  ["Contractor Web Design", "/contractor-web-design-colorado"],
  ["Auto Repair Marketing Colorado", "/auto-repair-marketing-colorado"],
  ["Auto Dealer Marketing Colorado", "/auto-dealer-marketing-colorado"],
  ["Dog Trainer Marketing Colorado", "/dog-trainer-marketing-colorado"],
  ["Ecommerce SEO Colorado", "/ecommerce-seo-colorado"],
];

const hubLinks: Array<[string, string]> = [
  ["Colorado Springs Headquarters", "/colorado-springs-headquarters"],
  ["Woodmen Rd Office & Google Profile", "/colorado-springs-office-woodmen-rd"],
  ["Commercial Contractor Marketing", "/commercial-contractor-marketing-colorado"],
  ["Home Builder & Remodeler Marketing", "/custom-home-builder-remodeler-marketing-colorado"],
  ["Broker & Property Manager Marketing", "/real-estate-broker-property-manager-marketing-colorado"],
  ["Pikes Peak Contractor Marketing", "/pikes-peak-contractor-marketing"],
  ["South Metro Contractor Marketing", "/south-metro-contractor-marketing"],
  ["Denver Roofing", "/denver-roofing-marketing"],
  ["Colorado Springs HVAC", "/colorado-springs-hvac-marketing"],
  ["Castle Rock Plumbing", "/castle-rock-plumbing-marketing"],
  ["Fort Collins Electrical", "/fort-collins-electrical-marketing"],
  ["Monument", "/monument"],
  ["Falcon & Peyton", "/falcon"],
  ["Woodland Park", "/woodland-park"],
  ["Highlands Ranch", "/highlands-ranch"],
  ["Lone Tree", "/lone-tree-digital-marketing"],
  ["Roofing Leads Guide", "/learn/roofing-leads-colorado"],
  ["HVAC Leads Guide", "/learn/hvac-leads-colorado"],
  ["Plumbing Leads Guide", "/learn/plumbing-leads-colorado"],
  ["Electrician Leads Guide", "/learn/electrician-leads-colorado"],
  ["Free Leads First Guide", "/learn/organic-leads-colorado-contractors"],
  ["Angi & Thumbtack Alternatives", "/learn/angi-thumbtack-alternatives-colorado"],
  ["Colorado SEO & Web Design Costs", "/colorado-marketing-seo-web-design-costs"],
  ["Colorado Web Design", "/colorado-web-design"],
  ["Colorado SEO", "/colorado-seo"],
  ["Concrete Leads Guide", "/learn/concrete-leads-colorado"],
  ["Fence & Deck Leads Guide", "/learn/fence-deck-leads-colorado"],
  ["Tree Care Leads Guide", "/learn/tree-care-leads-colorado"],
  ["Agency contracts Guide", "/learn/escape-marketing-agency-contracts-colorado"],
  ["SEO Guide", "/learn/page-10-to-page-1-seo-colorado"],
  ["Social media Guide", "/learn/social-media-views-colorado-business"],
  ["Google Ads Guide", "/learn/fix-google-ads-wasted-spend-colorado"],
  ["AI search Guide", "/learn/rank-in-ai-search-colorado"],
  ["Castle Rock Web Design Company", "/web-design-castle-rock-colorado"],
];

export function SiteFooter() {
  return (
    <footer className="border-t border-white/10 bg-brand-black text-brand-cream">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <Link to="/" className="inline-flex items-center" aria-label="Colorado Web Impressions home">
              <img
                src={footerLogo}
                alt="Colorado Web Impressions logo — Colorado Springs web design and SEO agency"
                width={256}
                height={96}
                className="h-16 w-auto"
                loading="lazy"
                decoding="async"
                data-geo-lat="38.9359976"
                data-geo-lng="-104.7653118"
                data-geo-city="Colorado Springs"
              />

            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-brand-cream/85">
              A Colorado-only digital marketing agency helping local businesses get more leads,
              calls, and booked jobs.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {socialLinks.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="grid h-9 w-9 place-items-center rounded-full border border-brand-cream/20 text-brand-cream transition-smooth hover:border-brand-gold hover:text-brand-gold"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-brand-gold">
              Services
            </h3>
            <ul className="space-y-2.5 text-sm">
              {[
                ["Web Design", "/colorado-springs-web-design"],
                ["SEO", "/colorado-springs-seo"],
                ["Google Maps Ranking", "/colorado-springs-google-maps"],
                ["PPC Advertising", "/colorado-springs-ppc"],
                ["Moving to Colorado Springs", "/moving-to-colorado-springs"],
                ["Digital Marketing", "/digital-marketing"],
                ["GEO & AI Search", "/geo-ai"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-brand-cream/85 transition-smooth hover:text-brand-gold"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-brand-gold">
              Company
            </h3>
            <ul className="space-y-2.5 text-sm">
              {[
                ["About Us", "/about"],
                ["Meet Chris (Owner)", "/meet-chris"],
                ["Case Studies", "/case-studies"],
                ["Editorial Policy", "/editorial-policy"],
                ["Reviews", "/reviews"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-brand-cream/85 transition-smooth hover:text-brand-gold"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-brand-gold">
              Resources
            </h3>
            <ul className="space-y-2.5 text-sm">
              {[
                ["Our Team", "/team"],
                ["How We Work", "/how-we-work"],
                ["Guarantees", "/guarantees"],
                ["Press & Media", "/press"],
                ["Featured In", "/featured-in"],
                ["FAQ", "/faq"],
                ["Answers Hub", "/answers"],
                ["Marketing Glossary", "/learn/glossary"],
                ["Community", "/community"],
                ["Changelog", "/changelog"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-brand-cream/85 transition-smooth hover:text-brand-gold"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-brand-gold">
              Get In Touch
            </h3>
            <ul className="space-y-3 text-sm text-brand-cream/80">
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 text-brand-gold" />
                <a href="tel:+17194193935" className="hover:text-brand-gold" aria-label="Call (719) 419-3935">
                  (719) 419-3935
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                <a href="mailto:info@coloradowebimpressions.com" className="min-w-0 break-all hover:text-brand-gold" aria-label="Email Colorado Web Impressions">
                  info@coloradowebimpressions.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-brand-gold" />
                <address className="not-italic leading-relaxed">
                  3210 E Woodmen Rd STE 110<br />
                  Colorado Springs, CO 80920
                </address>
              </li>
            </ul>
          </div>
        </div>

        {/* Sitewide internal-link boost rows — rendered eagerly so they
            always appear in the initial DOM for SEO and for users on the
            preview environment where lazy-mount triggers can be flaky. */}
        <nav
            aria-label="Top Colorado cities we serve"
            className="mt-12 border-t border-brand-cream/10 pt-8"
          >
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-brand-gold">
              Colorado Cities We Serve
            </h3>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-brand-cream/85">
              {cityLinks.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-brand-gold">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav
            aria-label="Contractor marketing hubs"
            className="mt-8 border-t border-brand-cream/10 pt-8"
          >
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-brand-gold">
              Contractor Marketing Hubs &amp; Guides
            </h3>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-brand-cream/85">
              {hubLinks.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-brand-gold">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav
            aria-label="Industries we serve"
            className="mt-8 border-t border-brand-cream/10 pt-8"
          >
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-brand-gold">
              Industry Marketing Specialists
            </h3>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-brand-cream/85">
              {industryLinks.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-brand-gold">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-brand-cream/10 pt-8 text-xs text-brand-cream/80 md:flex-row">
          <p>© {new Date().getFullYear()} Colorado Web Impressions. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/sitemap" className="hover:text-brand-gold">Site Map</Link>
            <Link to="/privacy" className="hover:text-brand-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-brand-gold">Terms</Link>
            <Link to="/sms-policy" className="hover:text-brand-gold">SMS Policy</Link>
            <Link to="/cookies" className="hover:text-brand-gold">Cookies</Link>
            <Link to="/disclaimer" className="hover:text-brand-gold">Disclaimer</Link>
            <Link to="/dmca" className="hover:text-brand-gold">DMCA</Link>
            <Link to="/do-not-sell" className="hover:text-brand-gold">Your Privacy Choices</Link>
            <Link to="/accessibility" className="hover:text-brand-gold">Accessibility</Link>
            <Link to="/editorial-policy" className="hover:text-brand-gold">Editorial</Link>
            <Link to="/learn" className="hover:text-brand-gold">Learn</Link>
            <a href="/rss.xml" className="hover:text-brand-gold">RSS</a>
          </div>
          <p>Proudly built in Colorado 🏔️</p>
        </div>
      </div>
    </footer>
  );
}
